import { useEffect } from 'react';
import { siteConfig } from '@/mocks/siteConfig';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEOHead({
  title = siteConfig.siteDescription,
  description = siteConfig.siteDescription,
  keywords = '秘密教学,秘密教学方法论,隐秘知识体系,非主流学习法,高阶认知训练,深度学习秘籍,突破学习瓶颈',
  canonicalUrl = siteConfig.siteUrl,
  ogType = 'website',
  jsonLd,
}: SEOHeadProps) {
  const pageTitle = title.includes(siteConfig.siteName)
    ? title
    : `${title} | ${siteConfig.siteName}`;

  useEffect(() => {
    document.title = pageTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('og:title', pageTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:locale', 'zh_CN', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', pageTitle);
    setMeta('twitter:description', description);

    const linkEl = document.querySelector('link[rel="canonical"]');
    if (linkEl) {
      linkEl.setAttribute('href', canonicalUrl);
    }

    return () => {
      const ldScripts = document.querySelectorAll('script[type="application/ld+json"]');
      ldScripts.forEach((s) => {
        if (s.getAttribute('data-dynamic') === 'true') s.remove();
      });
    };
  }, [pageTitle, description, keywords, canonicalUrl, ogType]);

  useEffect(() => {
    if (!jsonLd) return;
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-dynamic="true"]');
    existingScripts.forEach((s) => s.remove());

    const dataArray = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    dataArray.forEach((data) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-dynamic', 'true');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"][data-dynamic="true"]');
      scripts.forEach((s) => s.remove());
    };
  }, [jsonLd]);

  return null;
}